/**
 * AgreementGate
 *
 * Mounted once in the app shell. When the tenant owes the sign-up commitment, it takes over the
 * screen until they agree — that commitment is the condition for using the console at all.
 *
 * The machine-creation commitment is deliberately NOT raised here. It is asked at the moment it
 * applies, inside the creation flow, so a customer who only came to look at an invoice is not
 * stopped by a commitment about machines.
 */
import { useState } from "react";
import {
  useAcceptAgreement,
  useOutstandingAgreement,
} from "@/hooks/useAgreements";
import { AgreementDialog } from "./AgreementDialog";

export function AgreementGate() {
  const agreement = useOutstandingAgreement("signup_commitment");
  const acceptMutation = useAcceptAgreement();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  if (!agreement) {
    return null;
  }

  const handleAccept = (culture: string) => {
    setErrorKey(null);
    acceptMutation.mutate(
      { agreement, culture },
      {
        onError: (error: unknown) => {
          // A revision published while this dialog was open comes back as AGREEMENT_OUT_OF_DATE.
          // The refetch that follows swaps in the new text, so the customer reads what is
          // current rather than agreeing to what they happened to have on screen.
          const code =
            (error as { response?: { data?: { error?: { code?: string } } } })
              ?.response?.data?.error?.code ?? "";

          setErrorKey(
            code === "AGREEMENT_OUT_OF_DATE"
              ? "agreements.outOfDate"
              : "agreements.acceptFailed",
          );
        },
      },
    );
  };

  return (
    <AgreementDialog
      open
      agreement={agreement}
      isAccepting={acceptMutation.isPending}
      errorKey={errorKey}
      onAccept={handleAccept}
    />
  );
}
